import { useCallback, useEffect, useState } from "react";

import { DEFAULT_TAG_KEY, TAG_KEY_STORAGE_KEY } from "../helpers/constants";

const TAG_KEY_CHANGE_EVENT = "tagKeyChanged";

export const getStoredTagKey = (): string => {
  try {
    const stored = localStorage.getItem(TAG_KEY_STORAGE_KEY);
    return stored || DEFAULT_TAG_KEY;
  } catch (error) {
    console.warn("Failed to read tag key from localStorage:", error);
    return DEFAULT_TAG_KEY;
  }
};

export const useTagKey = () => {
  const [tagKey, setTagKeyState] = useState<string>(getStoredTagKey);

  const setTagKey = useCallback((newTagKey: string) => {
    try {
      localStorage.setItem(TAG_KEY_STORAGE_KEY, newTagKey);
      setTagKeyState(newTagKey);

      // This event is necessary to make it so that the cells in the table update right away when the tag key changes
      window.dispatchEvent(
        new CustomEvent(TAG_KEY_CHANGE_EVENT, {
          detail: { newTagKey },
        }),
      );
    } catch (error) {
      console.warn("Failed to save tag key to localStorage:", error);
    }
  }, []);

  useEffect(() => {
    const handleTagKeyChange = (event: CustomEvent) => {
      const newTagKey = event.detail.newTagKey;
      setTagKeyState(newTagKey);
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === TAG_KEY_STORAGE_KEY && event.newValue !== null) {
        setTagKeyState(event.newValue);
      }
    };

    window.addEventListener(
      TAG_KEY_CHANGE_EVENT,
      handleTagKeyChange as EventListener,
    );
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener(
        TAG_KEY_CHANGE_EVENT,
        handleTagKeyChange as EventListener,
      );
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  return { tagKey, setTagKey };
};
